import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import { SEOHero, SEOSection, FeatureGrid, BenefitList, InternalLinkGrid, CTABanner } from '../../components/SEOPageComponents';
import { Home as HomeIcon, Receipt, Shield, Zap, Users, BarChart3, Clock, CheckCircle2 } from 'lucide-react';

export default function AirbnbEInvoiceMalaysia() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <>
      <SEO
        title="Airbnb e-Invoice Malaysia — LHDN Invoicing for Short-Term Rental Hosts"
        description="Automate LHDN e-invoicing for your Airbnb and short-term rental properties in Malaysia. StayMii handles multi-property invoicing, OTA data sync, and compliance — so you don't have to."
        keywords="airbnb e invoice malaysia, airbnb invoicing malaysia, short term rental e invoice, airbnb lhdn, airbnb tax malaysia, short term rental invoicing system, vacation rental e invoice, homestay e invoice malaysia"
        canonicalUrl="/airbnb-e-invoice-malaysia"
      />
      <div className="min-h-screen bg-white">
        <LandingNavBar />
        <SEOHero
          badge="Airbnb & Short-Term Rental"
          h1="Airbnb e-Invoice Malaysia: LHDN Compliance for Short-Term Rental Hosts"
          subtitle="Running Airbnb properties or short-term rentals in Malaysia? LHDN requires e-invoices for every booking. StayMii automates your e-invoicing — multiple properties, multiple OTAs, one dashboard."
        />

        <SEOSection h2="Do Airbnb Hosts Need e-Invoice in Malaysia?" description="Yes. Under LHDN's e-invoice mandate, all businesses — including Airbnb hosts, homestay operators, and short-term rental managers — must submit digital invoices for every transaction.">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-900 mb-4">The Challenge for Airbnb Hosts</h3>
              <BenefitList items={[
                { title: 'Multiple Properties', desc: 'Managing e-invoices across 5, 10, or 50+ properties is nearly impossible manually.' },
                { title: 'Multiple OTA Platforms', desc: 'Bookings come from Airbnb, Booking.com, Agoda — each needs a separate compliant invoice.' },
                { title: 'Tax Complexity', desc: 'SST, tourism tax, and service charge calculations differ by property type and location.' },
                { title: 'LHDN Compliance Risk', desc: 'Non-compliance penalties of up to RM200,000 apply to all businesses, including short-term rental operators.' },
              ]} />
            </div>
            <div className="bg-[#0F172A] rounded-2xl p-8 border border-blue-500/20">
              <h3 className="text-lg font-bold text-white mb-4">How StayMii Solves It</h3>
              <div className="space-y-4">
                {[
                  'Automatically syncs bookings from all your OTA platforms',
                  'Generates LHDN-compliant e-invoices for every reservation',
                  'Handles multi-property invoicing from a single dashboard',
                  'Calculates SST, tourism tax, and service charges automatically',
                  'Submits directly to LHDN MyInvois API',
                  'Works with or without a PMS — StayMii Lite is included'
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-white/80">{t}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </SEOSection>

        <SEOSection h2="e-Invoice Features for Airbnb & Short-Term Rental Operators" bg="bg-slate-50">
          <FeatureGrid items={[
            { icon: HomeIcon, title: 'Multi-Property Management', desc: 'Manage e-invoices for all your properties from one dashboard. Whether you have 3 or 300 listings.' },
            { icon: Receipt, title: 'Automatic Invoice Generation', desc: 'Every Airbnb booking becomes a compliant e-invoice. No manual data entry required.' },
            { icon: Shield, title: 'LHDN Compliant', desc: 'All invoices meet LHDN e-invoice specifications. Validated before submission to prevent rejections.' },
            { icon: Users, title: 'Guest Tax Receipts', desc: 'Automatically generate and send tax-compliant receipts to guests after checkout.' },
            { icon: BarChart3, title: 'Revenue Analytics', desc: 'Track revenue by property, by OTA channel, and by time period. Exportable for tax filing.' },
            { icon: Clock, title: 'No PMS Required', desc: "Don't have a PMS? StayMii Lite includes basic property management built in. Start immediately." },
          ]} />
        </SEOSection>

        <SEOSection h2="Related Solutions">
          <InternalLinkGrid links={[
            { to: '/e-invoice-malaysia', title: 'e-Invoice Malaysia', desc: 'Complete e-invoicing guide' },
            { to: '/lhdn-e-invoice', title: 'LHDN e-Invoice', desc: 'Compliance requirements' },
            { to: '/channel-manager-e-invoice', title: 'Channel Manager e-Invoice', desc: 'OTA booking automation' },
            { to: '/hotel-e-invoice-system', title: 'Hotel e-Invoice System', desc: 'For hotel operations' },
            { to: '/pms-e-invoice-integration', title: 'PMS Integration', desc: 'Connect your PMS' },
            { to: '/myinvois-api', title: 'MyInvois API', desc: 'Technical API details' },
          ]} />
        </SEOSection>

        <CTABanner title="Automate e-Invoicing for Your Airbnb Properties" subtitle="Join Malaysian short-term rental operators using StayMii for LHDN compliance." />
        <LandingFooter />
      </div>
    </>
  );
}
